import {Question, Questionnaires} from '$api/patientList/questionnaire';
import {FormikErrors, FormikValues} from 'formik';
import * as Yup from 'yup';

// Function to generate Yup schema dynamically for questionnaires
export const generateValidationSchema = (questions: Question[]) => {
  const schemaFields: {[key: string]: Yup.AnySchema} = {};

  const addQuestionToSchema = (
    question: Question,
    prefix: string = '',
    parentKey?: string
  ) => {
    const questionKey = `${prefix}${question.id}`;

    // By default, all questions are optional
    let baseSchema: Yup.AnySchema;
    switch (question.question_type) {
      case 'radio': {
        const stringSchema = Yup.string().notRequired();
        baseSchema = parentKey
          ? stringSchema.when(parentKey, (parentValue: unknown, schema) => {
              return parentValue === 'Yes' ||
                (Array.isArray(parentValue) && parentValue.includes('Yes'))
                ? schema.required('This field is required')
                : schema.notRequired();
            })
          : stringSchema;
        break;
      }
      case 'textField': {
        const stringSchema = Yup.string().notRequired();
        baseSchema = parentKey
          ? stringSchema.when(parentKey, (parentValue: unknown, schema) => {
              return parentValue === 'Yes' ||
                (Array.isArray(parentValue) && parentValue.includes('Yes'))
                ? schema.required('This field is required')
                : schema.notRequired();
            })
          : stringSchema;
        break;
      }
      case 'selectYear': {
        const stringSchema = Yup.string().notRequired();
        baseSchema = parentKey
          ? stringSchema.when(parentKey, (parentValue: unknown, schema) => {
              return parentValue === 'Yes' ||
                (Array.isArray(parentValue) && parentValue.includes('Yes'))
                ? schema.required('This field is required')
                : schema.notRequired();
            })
          : stringSchema;
        break;
      }
      case 'multiText': {
        const arraySchema = Yup.array()
          .of(Yup.string().notRequired())
          .notRequired();
        baseSchema = parentKey
          ? arraySchema.when(parentKey, (parentValue: unknown, schema) => {
              return parentValue === 'Yes' ||
                (Array.isArray(parentValue) && parentValue.includes('Yes'))
                ? schema.required('This field is required')
                : schema.notRequired();
            })
          : arraySchema;
        break;
      }
      case 'date': {
        const dateSchema = Yup.date().notRequired();
        baseSchema = parentKey
          ? dateSchema.when(parentKey, (parentValue: unknown, schema) => {
              return parentValue === 'Yes' ||
                (Array.isArray(parentValue) && parentValue.includes('Yes'))
                ? schema.required('This field is required')
                : schema.notRequired();
            })
          : dateSchema;
        break;
      }
      default: {
        baseSchema = Yup.mixed().notRequired();
        break;
      }
    }

    schemaFields[questionKey] = baseSchema;

    // Recursively add sub-questions to the schema
    if (question.sub_questions && question.sub_questions.length > 0) {
      question.sub_questions.forEach(subQuestion =>
        addQuestionToSchema(subQuestion, `${questionKey}_`, questionKey)
      );
    }
  };

  questions.forEach(question => addQuestionToSchema(question));

  const schema = Yup.object().shape(schemaFields);

  return schema;
};

// Function to generate initial values for questionnaires dynamically
export const generateInitialValues = (questions: Question[]) => {
  const initialValues: {[key: string]: string | string[]} = {};

  const addQuestionToInitialValues = (
    question: Question,
    prefix: string = ''
  ) => {
    const questionKey = `${prefix}${question.id}`;

    if (question.question_type === 'multiText') {
      if (Array.isArray(question.answer)) {
        initialValues[questionKey] = question.answer;
      } else {
        initialValues[questionKey] = question.answer || '';
      }
    } else {
      initialValues[questionKey] = question.answer || '';
    }

    // Recursively add sub-questions to the initial values
    if (question.sub_questions && question.sub_questions.length > 0) {
      question.sub_questions.forEach(subQuestion =>
        addQuestionToInitialValues(subQuestion, `${questionKey}_`)
      );
    }
  };

  questions.forEach(question => addQuestionToInitialValues(question));

  return initialValues;
};

export const getErrorMessage = (
  error:
    | string
    | string[]
    | FormikErrors<unknown>
    | FormikErrors<unknown>[]
    | undefined
): string => {
  if (typeof error === 'string') {
    return error;
  } else if (Array.isArray(error)) {
    return error.join(', ');
  } else if (typeof error === 'object') {
    return JSON.stringify(error);
  }
  return '';
};

export const generateQuestionnairePayload = (
  values: FormikValues
): Questionnaires[] =>
  Object.entries(values).map(([key, value]) => {
    const idParts = key.split('_');
    const questionId = Number(idParts[idParts.length - 1]);

    return {id: questionId, answer: value};
  });

export const getSubQuestionnaireId = ({
  question,
  subQuestion,
}: {
  question: Question;
  subQuestion: Question;
}): string => {
  return question.id + '_' + subQuestion.id;
};

export interface paginateQuestionnairesResults {
  questionsToDisplay: Question[];
  totalPages: number;
  startIndex: number;
  endIndex: number;
}

export function paginateQuestionnaires(
  items: Question[],
  currentPage: number,
  itemsPerPage: number
): paginateQuestionnairesResults {
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const questionsToDisplay = items.slice(startIndex, endIndex);
  const totalPages = Math.ceil(items.length / itemsPerPage);

  return {
    questionsToDisplay,
    totalPages,
    startIndex,
    endIndex,
  };
}
